import React, { useState, useEffect, useCallback } from "react";
import { ThemeContext } from "../../themes/ThemeContext";
import { 
  getSuccessfulRefunds, 
  getFailedRefunds, 
  processRefund, 
  exportSuccessfulRefunds,
  exportFailedRefunds,
  getAllBanks,
  getBankName
} from "../../data/api/api";
import { FaCheckCircle, FaPlus } from "react-icons/fa";
import { BiTrendingDown, BiTrendingUp } from "react-icons/bi";
import NoData from "../../components/NoData/NoData";
import StatBox from "./components/StatBox";
import TableSection from "./components/TableSection";
import Loader from "../../components/Loader/Loader";
import { useTestPartner } from "../../hooks/useTestPartner";
import showToast from "../../utils/toastUtils";

const RefundsDashboard = () => {
  const theme = React.useContext(ThemeContext);
  const { isTestPartner } = useTestPartner();

  const [refundStats, setRefundStats] = useState({
    totalSuccessful: 0,
    totalFailed: 0,
    totalAmount: 0
  });

  const [successfulRefunds, setSuccessfulRefunds] = useState([]);
  const [failedRefunds, setFailedRefunds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingPagination, setIsUpdatingPagination] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const LOAD_TIMEOUT_MS = 30000;

  // Pagination states
  const [successfulPagination, setSuccessfulPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  const [failedPagination, setFailedPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });


  // Filter states
  const [successfulFilters, setSuccessfulFilters] = useState({
    start_date: '',
    end_date: '',
    refund_type: '',
    sort_by: 'processed_at',
    sort_order: 'desc'
  });

  const [failedFilters, setFailedFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });


  // Process refund modal state
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processForm, setProcessForm] = useState({
    original_transaction_ref: '',
    refund_amount: '',
    refund_type: 'full',
    refund_method: 'transfer',
    refund_reason: '',
    providus: false,
    acct_no: '',
    acct_name: '',
    bank_name: '',
    bank_code: '',
    provider_payment_id: ''
  });

  // Banking flow state
  const [bankingStep, setBankingStep] = useState(1); // 1: Bank Selection, 2: Account Entry, 3: Verification, 4: Confirmation, 5: Processing
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [verifiedAccountName, setVerifiedAccountName] = useState('');
  const [accountVerificationError, setAccountVerificationError] = useState('');

  // Banks list for dropdown
  const [banks, setBanks] = useState([]);

  const fetchSuccessfulRefunds = useCallback(async () => {
    try {
      setIsUpdatingPagination(true);
      const response = await getSuccessfulRefunds({
        ...successfulFilters,
        page: successfulPagination.currentPage
      });

      if (response.success) {
        const rawData = response.data.data || [];
        const processedRefunds = rawData.map((refund) => ({
          id: refund.id,
          refund_id: refund.refund_id,
          refund_amount: parseFloat(refund.refund_amount),
          refund_type: refund.refund_type,
          refund_method: refund.refund_method,
          charges: parseFloat(refund.charges || 0),
          destination_acct: refund.destination_acct,
          destination_acct_name: refund.destination_acct_name,
          destination_bank: refund.destination_bank,
          original_transaction_ref: refund.original_transaction_ref,
          refund_reason: refund.refund_reason,
          processed_at: refund.processed_at,
          created_at: refund.created_at,
          status: 'completed'
        }));

        setSuccessfulRefunds(processedRefunds);
        setSuccessfulPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      } else {
        // console.error("Failed to fetch successful refunds:", response.message);
        setError(`Failed to fetch successful refunds: ${response.message}`);
      }
    } catch (error) {
      // console.error("Failed to fetch successful refunds:", error);
      setError("Failed to fetch successful refunds. Please check if the refunds API is available.");
    }
    setIsUpdatingPagination(false);
  }, [successfulFilters, successfulPagination.currentPage]);

  const fetchFailedRefunds = useCallback(async () => {
    try {
      setIsUpdatingPagination(true);
      const response = await getFailedRefunds({
        ...failedFilters,
        page: failedPagination.currentPage
      });

      if (response.success) {
        const processedRefunds = response.data.data.map((refund) => ({
          id: refund.id,
          refund_id: refund.trans_ref,
          refund_amount: parseFloat(refund.refund_amount),
          refund_type: refund.refund_type,
          refund_method: refund.refund_method,
          charges: parseFloat(refund.charges || 0),
          dest_account: refund.dest_account,
          dest_acct_name: refund.dest_acct_name,
          dest_bank: refund.dest_bank,
          original_transaction_ref: refund.original_transaction_ref,
          refund_reason: refund.refund_reason,
          status: refund.status,
          error_log: refund.error_log,
          created_at: refund.created_at
        }));

        setFailedRefunds(processedRefunds);
        setFailedPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      // console.error("Failed to fetch failed refunds:", error);
    } finally {
      setIsUpdatingPagination(false);
    }
  }, [failedFilters, failedPagination.currentPage]);


  const fetchBanks = useCallback(async () => {
    try {
      const response = await getAllBanks();
      if (response.success) {
        setBanks(response.data || []);
      } else {
        // console.error("Failed to fetch banks:", response.message);
        setBanks([]);
      }
    } catch (error) {
      // console.error("Failed to fetch banks:", error);
      setBanks([]);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setError("");

    try {
      await Promise.all([
        fetchSuccessfulRefunds(),
        fetchFailedRefunds(),
        fetchBanks()
      ]);
    } catch (error) {
      // console.error("Error fetching refunds data:", error);
      setError("Failed to fetch refunds data. The refunds API might not be available yet. Please contact support if this issue persists.");
    }
  }, [fetchBanks, fetchFailedRefunds, fetchSuccessfulRefunds]);

  const handleAccountVerification = async () => {
    if (!processForm.bank_code || !processForm.acct_no) {
      setAccountVerificationError("Please select a bank and enter account number");
      return;
    }

    setIsVerifyingAccount(true);
    setAccountVerificationError("");

    try {
      const response = await getBankName(processForm.bank_code, processForm.acct_no);
      
      if (response.success) {
        setVerifiedAccountName(response.account_name);
        setProcessForm(prev => ({ ...prev, acct_name: response.account_name }));
        setBankingStep(4); // Move to confirmation step
      } else {
        setAccountVerificationError(response.message);
      }
    } catch (error) {
      setAccountVerificationError("Account verification failed. Please try again.");
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  const handleProcessRefund = async () => {
    if (isTestPartner) {
      showToast("error", "Refund processing is not available in test mode.", 3000);
      setError("Refund processing is not available in test mode.");
      return;
    }

    setIsProcessing(true);
    setError("");
    setSuccess("");

    try {
      const response = await processRefund(processForm);
      
      if (response.status || response.success) {
        setSuccess(response.message);
        setShowProcessModal(false);
        setProcessForm({
          original_transaction_ref: '',
          refund_amount: '',
          refund_type: 'full',
          refund_method: 'transfer',
          refund_reason: '',
          providus: false,
          acct_no: '',
          acct_name: '',
          bank_name: '',
          bank_code: '',
          provider_payment_id: ''
        });
        setBankingStep(1);
        setVerifiedAccountName('');
        setAccountVerificationError('');
        await fetchAllData();
      } else {
        const errorMessage = response.message || response.error?.message || "Failed to process refund. Please try again.";
        setError(errorMessage);
        if (response.error === "READ_ONLY_ACCESS") {
          showToast("error", errorMessage, 3000);
        }
      }
    } catch (error) {
      setError("Failed to process refund. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setShowProcessModal(false);
    setBankingStep(1);
    setVerifiedAccountName('');
    setAccountVerificationError('');
    setProcessForm({
      original_transaction_ref: '',
      refund_amount: '',
      refund_type: 'full',
      refund_method: 'transfer',
      refund_reason: '',
      providus: false,
      acct_no: '',
      acct_name: '',
      bank_name: '',
      bank_code: '',
      provider_payment_id: ''
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAllData();
    setIsRefreshing(false);
  };

  const handleSuccessfulExport = async () => {
    try {
      const response = await exportSuccessfulRefunds(successfulFilters);
      if (response.success) {
        setSuccess(response.message);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Export failed. Please try again.");
    }
  };

  const handleFailedExport = async () => {
    try {
      const response = await exportFailedRefunds(failedFilters);
      if (response.success) {
        setSuccess(response.message);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Export failed. Please try again.");
    }
  };

  // Handle page changes
  const handleSuccessfulPageChange = (page) => {
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) return;
    
    const total = parseInt(successfulPagination.total, 10) || 0;
    const perPage = parseInt(successfulPagination.perPage, 10) || 10;
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    setSuccessfulPagination(prev => ({
      ...prev,
      currentPage: validPage
    }));
  };

  const handleFailedPageChange = (page) => {
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) return;
    
    const total = parseInt(failedPagination.total, 10) || 0;
    const perPage = parseInt(failedPagination.perPage, 10) || 10;
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    setFailedPagination(prev => ({
      ...prev,
      currentPage: validPage
    }));
  };


  // Handle rows per page changes
  const handleSuccessfulRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setSuccessfulPagination(prev => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };

  const handleFailedRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setFailedPagination(prev => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };


  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. The server may be slow or unreachable.")), LOAD_TIMEOUT_MS)
      );
      await Promise.race([fetchAllData(), timeoutPromise]);
    } catch (error) {
      setLoadError(error?.message || "Failed to load dashboard. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchSuccessfulRefunds();
  }, [fetchSuccessfulRefunds, successfulFilters, successfulPagination.currentPage, successfulPagination.perPage]);

  useEffect(() => {
    fetchFailedRefunds();
  }, [failedFilters, failedPagination.currentPage, failedPagination.perPage, fetchFailedRefunds]);


  // Calculate stats
  useEffect(() => {
    const totalSuccessful = successfulRefunds.length;
    const totalFailed = failedRefunds.length;
    const totalAmount = successfulRefunds.reduce((sum, refund) => sum + refund.refund_amount, 0);

    setRefundStats({
      totalSuccessful,
      totalFailed,
      totalAmount
    });
  }, [successfulRefunds, failedRefunds]);

  // Column definitions
  const successfulColumns = [
    { key: 'refund_id', label: 'Refund ID' },
    { key: 'refund_amount', label: 'Amount', format: (value) => `₦${value.toLocaleString()}` },
    { key: 'refund_type', label: 'Type' },
    { key: 'refund_method', label: 'Method' },
    { key: 'original_transaction_ref', label: 'Transaction Ref' },
    { key: 'destination_acct_name', label: 'Account Name' },
    { key: 'destination_bank', label: 'Bank' },
    { key: 'processed_at', label: 'Processed Date', format: (value) => new Date(value).toLocaleDateString() }
  ];

  const failedColumns = [
    { key: 'refund_id', label: 'Refund ID' },
    { key: 'refund_amount', label: 'Amount', format: (value) => `₦${value.toLocaleString()}` },
    { key: 'refund_type', label: 'Type' },
    { key: 'refund_method', label: 'Method' },
    { key: 'original_transaction_ref', label: 'Transaction Ref' },
    { key: 'dest_acct_name', label: 'Account Name' },
    { key: 'dest_bank', label: 'Bank' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Created Date', format: (value) => new Date(value).toLocaleDateString() }
  ];


  const renderProcessModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.colors.background.secondary }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
            Process New Refund
          </h3>
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= bankingStep
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
            Step {bankingStep} of 5: {
              bankingStep === 1 ? 'Transaction Details' :
              bankingStep === 2 ? 'Bank Selection' :
              bankingStep === 3 ? 'Account Entry' :
              bankingStep === 4 ? 'Account Confirmation' :
              'Refund Method & Summary'
            }
          </div>
        </div>
        
        {/* Step 1: Transaction Details */}
        {bankingStep === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Payment Reference *
              </label>
              <input
                type="text"
                value={processForm.original_transaction_ref}
                onChange={(e) => setProcessForm(prev => ({ ...prev, original_transaction_ref: e.target.value }))}
                className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ 
                  backgroundColor: theme.colors.background.primary, 
                  borderColor: "#0a0d15",
                  color: theme.colors.text.primary
                }}
                placeholder="NIPEXT202410261673 or a8a448b6-a325-4d7d-84b7-7c53928dd0b5"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Refund Amount *
                </label>
                <input
                  type="number"
                  value={processForm.refund_amount}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, refund_amount: e.target.value }))}
                  className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ 
                    backgroundColor: theme.colors.background.primary, 
                    borderColor: "#0a0d15",
                    color: theme.colors.text.primary
                  }}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Refund Type *
                </label>
                <select
                  value={processForm.refund_type}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, refund_type: e.target.value }))}
                  className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ 
                    backgroundColor: theme.colors.background.primary, 
                    borderColor: "#0a0d15",
                    color: theme.colors.text.primary
                  }}
                >
                  <option value="full">Full Refund</option>
                  <option value="partial">Partial Refund</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Refund Reason *
              </label>
              <textarea
                value={processForm.refund_reason}
                onChange={(e) => setProcessForm(prev => ({ ...prev, refund_reason: e.target.value }))}
                className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ 
                  backgroundColor: theme.colors.background.primary, 
                  borderColor: "#0a0d15",
                  color: theme.colors.text.primary
                }}
                rows="3"
                placeholder="Enter reason for refund"
              />
            </div>
          </div>
        )}

        {/* Step 2: Bank Selection */}
        {bankingStep === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Select Bank *
              </label>
              <select
                value={processForm.bank_code}
                onChange={(e) => {
                  const selectedBank = banks.find(bank => bank.bankCode === e.target.value);
                  const isProvidus = selectedBank?.bankName?.toLowerCase().includes('providus');
                  setProcessForm(prev => ({ 
                    ...prev, 
                    bank_code: e.target.value,
                    bank_name: selectedBank?.bankName || '',
                    providus: isProvidus
                  }));
                }}
                className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ 
                  backgroundColor: theme.colors.background.primary, 
                  borderColor: "#0a0d15",
                  color: theme.colors.text.primary
                }}
              >
                <option value="">Select Bank</option>
                {banks
                  .filter((bank, index, self) => 
                    index === self.findIndex(b => b.bankCode === bank.bankCode)
                  )
                  .map((bank, index) => (
                    <option key={`${bank.bankCode}-${index}`} value={bank.bankCode}>
                      {bank.bankName}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 3: Account Entry */}
        {bankingStep === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Account Number *
              </label>
              <input
                type="text"
                value={processForm.acct_no}
                onChange={(e) => setProcessForm(prev => ({ ...prev, acct_no: e.target.value }))}
                className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ 
                  backgroundColor: theme.colors.background.primary, 
                  borderColor: "#0a0d15",
                  color: theme.colors.text.primary
                }}
                placeholder="Enter account number"
              />
            </div>
            {accountVerificationError && (
              <div className="text-red-500 text-sm">{accountVerificationError}</div>
            )}
          </div>
        )}

        {/* Step 4: Account Confirmation */}
        {bankingStep === 4 && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border" style={{ backgroundColor: theme.colors.background.primary, borderColor: "#0a0d15" }}>
              <h4 className="font-medium mb-2" style={{ color: theme.colors.text.primary }}>Account Verification</h4>
              <div className="space-y-2 text-sm" style={{ color: theme.colors.background.grey }}>
                <div><span className="font-medium">Bank:</span> {processForm.bank_name}</div>
                <div><span className="font-medium">Account Number:</span> {processForm.acct_no}</div>
                <div><span className="font-medium">Account Name:</span> {verifiedAccountName}</div>
              </div>
            </div>
            <div className="text-sm" style={{ color: theme.colors.text.primary }}>
              Please confirm that the account details above are correct before proceeding with the refund.
            </div>
          </div>
        )}

        {/* Step 5: Refund Method Selection */}
        {bankingStep === 5 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Refund Method *
              </label>
              <select
                value={processForm.refund_method}
                onChange={(e) => setProcessForm(prev => ({ ...prev, refund_method: e.target.value }))}
                className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ 
                  backgroundColor: theme.colors.background.primary, 
                  borderColor: "#0a0d15",
                  color: theme.colors.text.primary
                }}
              >
                <option value="transfer">Transfer</option>
                <option value="reversal">Reversal</option>
              </select>
            </div>
            
            <div className="p-4 rounded-lg border" style={{ backgroundColor: theme.colors.background.primary, borderColor: "#0a0d15" }}>
              <h4 className="font-medium mb-2" style={{ color: theme.colors.text.primary }}>Refund Summary</h4>
              <div className="space-y-2 text-sm" style={{ color: theme.colors.text.secondary }}>
                <div><span className="font-medium">Payment Reference:</span> {processForm.original_transaction_ref || 'Not provided'}</div>
                <div><span className="font-medium">Refund Amount:</span> ₦{processForm.refund_amount || '0.00'}</div>
                <div><span className="font-medium">Refund Type:</span> {processForm.refund_type}</div>
                <div><span className="font-medium">Refund Method:</span> {processForm.refund_method}</div>
                <div><span className="font-medium">Bank:</span> {processForm.bank_name}</div>
                <div><span className="font-medium">Account:</span> {processForm.acct_no} - {processForm.acct_name}</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <div>
            {bankingStep > 1 && (
              <button
                onClick={() => setBankingStep(bankingStep - 1)}
                className="px-4 py-2 rounded text-white font-medium mr-3"
                style={{ 
                  backgroundColor: theme.colors.background.accent,
                  borderColor: "#0a0d15"
                }}
                disabled={isProcessing}
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 rounded text-white font-medium"
              style={{ 
                backgroundColor: theme.colors.background.accent,
                borderColor: "#0a0d15"
              }}
              disabled={isProcessing}
            >
              Cancel
            </button>
            
            {bankingStep < 5 ? (
              <button
                onClick={() => {
                  if (bankingStep === 3) {
                    handleAccountVerification();
                  } else {
                    setBankingStep(bankingStep + 1);
                  }
                }}
                disabled={
                  isTestPartner ||
                  isProcessing || 
                  isVerifyingAccount ||
                  (bankingStep === 1 && (!processForm.original_transaction_ref || !processForm.refund_amount || !processForm.refund_reason)) ||
                  (bankingStep === 2 && !processForm.bank_code) ||
                  (bankingStep === 3 && !processForm.acct_no) ||
                  (bankingStep === 5 && !processForm.refund_method)
                }
                className="px-4 py-2 rounded text-white font-medium"
                style={{ 
                  backgroundColor: (isTestPartner || isProcessing || isVerifyingAccount) ? "#4a5568" : "#0a0d15"
                }}
                title={isTestPartner ? "Write operations are disabled for test accounts" : ""}
              >
                {bankingStep === 3 ? (isVerifyingAccount ? 'Verifying...' : 'Verify Account') : 'Next'}
              </button>
            ) : (
              <button
                onClick={handleProcessRefund}
                disabled={isTestPartner || isProcessing}
                className="px-4 py-2 rounded text-white font-medium"
                style={{ 
                  backgroundColor: (isTestPartner || isProcessing) ? "#4a5568" : "#0a0d15"
                }}
                title={isTestPartner ? "Write operations are disabled for test accounts" : ""}
              >
                {isProcessing ? 'Processing...' : 'Process Refund'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loadError) {
    return (
      <section className="mt-20 min-h-screen">
        <div className="flex flex-col items-center justify-center py-20" style={{ color: theme?.colors?.text?.primary }}>
          <p className="text-red-500 mb-4 text-center max-w-md">{loadError}</p>
          <button
            onClick={fetchInitialData}
            className="px-6 py-2 rounded-lg font-medium transition-colors"
            style={{
              background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)",
              color: "#fff",
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="m-2 md:m-10 mt-24 p-2 md:p-10 min-h-screen flex items-center justify-center">
        <Loader message="Loading refunds dashboard..." />
      </section>
    );
  }

  return (
    <section className="mt-20 min-h-screen">
      {/* Stats Section */}
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mt-8">
        <StatBox 
          title="Total Refunded" 
          value={`₦${refundStats.totalAmount.toLocaleString()}`} 
          theme={theme} 
          icon={<FaCheckCircle color="#10B981" />} 
          type="balance"
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          extra={`${refundStats.totalSuccessful.toLocaleString()} successful refunds`}
        />
        
        <StatBox 
          title="Successful Refunds" 
          value={`${refundStats.totalSuccessful.toLocaleString()}`} 
          theme={theme} 
          icon={<BiTrendingUp className="text-green-500" />} 
          isPositive 
          type="inflow"
          extra="Completed transactions"
        />
        
        <StatBox 
          title="Failed Refunds" 
          value={`${refundStats.totalFailed.toLocaleString()}`} 
          theme={theme} 
          icon={<BiTrendingDown className="text-red-500" />} 
          isNegative 
          type="outflow"
          extra="Require attention"
        />
      </div>

      {/* Process Refund Button */}
      <div className="mt-8 flex justify-end">
        {isTestPartner ? (
          <div className="text-center py-4 text-gray-400 text-sm">
            <p>Refund processing is not available for test accounts.</p>
            <p className="text-xs mt-2">Contact your administrator for assistance.</p>
          </div>
        ) : (
          <button
            onClick={() => setShowProcessModal(true)}
            className="flex items-center space-x-2 px-6 py-3 text-white rounded-lg transition-colors"
            style={{ backgroundColor: "#0a0d15" }}
          >
            <FaPlus className="w-4 h-4" />
            <span>Process New Refund</span>
          </button>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
          <div className="text-red-500 text-sm">{error}</div>
        </div>
      )}
      
      {success && (
        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
          <div className="text-green-500 text-sm">{success}</div>
        </div>
      )}

      {/* Successful Refunds Table */}
      <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Successful Refunds</h3>
        </div>
        
        {isUpdatingPagination ? (
          <Loader message="Loading successful refunds..." />
        ) : successfulRefunds.length === 0 ? (
          <NoData message="No successful refunds available" />
        ) : (
          <TableSection 
            data={successfulRefunds} 
            columns={successfulColumns} 
            theme={theme} 
            pagination={successfulPagination}
            onPageChange={handleSuccessfulPageChange}
            onRowsPerPageChange={handleSuccessfulRowsPerPageChange}
            onExport={handleSuccessfulExport}
            showFilters={true}
            showExport={true}
            filters={successfulFilters}
            onFiltersChange={setSuccessfulFilters}
            defaultFilters={{
              sort_by: 'processed_at',
              sort_order: 'desc',
              refund_type: ''
            }}
            sortFieldOptions={[
              { value: 'processed_at', label: 'Processed Date' },
              { value: 'refund_amount', label: 'Amount' },
              { value: 'refund_type', label: 'Type' },
              { value: 'created_at', label: 'Created At' }
            ]}
          />
        )}
      </div>

      {/* Failed Refunds Table */}
      <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Failed Refunds</h3>
        </div>
        
        {isUpdatingPagination ? (
          <Loader message="Loading failed refunds..." />
        ) : failedRefunds.length === 0 ? (
          <NoData message="No failed refunds available" />
        ) : (
          <TableSection 
            data={failedRefunds} 
            columns={failedColumns} 
            theme={theme} 
            pagination={failedPagination}
            onPageChange={handleFailedPageChange}
            onRowsPerPageChange={handleFailedRowsPerPageChange}
            onExport={handleFailedExport}
            showFilters={true}
            showExport={true}
            filters={failedFilters}
            onFiltersChange={setFailedFilters}
            defaultFilters={{
              sort_by: 'created_at',
              sort_order: 'desc',
              status: ''
            }}
            sortFieldOptions={[
              { value: 'created_at', label: 'Created Date' },
              { value: 'refund_amount', label: 'Amount' },
              { value: 'status', label: 'Status' }
            ]}
          />
        )}
      </div>


      {/* Process Refund Modal */}
      {showProcessModal && renderProcessModal()}
    </section>
  );
};

export default RefundsDashboard;